import { captureEvent, type RegistrationMethod } from "@/shared/analytics/analytics";

export type { RegistrationMethod } from "@/shared/analytics/analytics";

const REGISTRATION_STORAGE_KEY = "pw-registration-analytics:v1";
const REGISTRATION_REPORTED_KEY = "pw-registration-analytics-reported:v1";

interface RegistrationProgress {
  method: RegistrationMethod;
  completed: boolean;
}

function readProgress(): RegistrationProgress | null {
  try {
    const raw = sessionStorage.getItem(REGISTRATION_STORAGE_KEY);
    if (!raw) return null;
    const value: unknown = JSON.parse(raw);
    if (!value || typeof value !== "object") return null;
    const method = "method" in value ? value.method : null;
    const completed = "completed" in value ? value.completed : null;
    if (
      method !== "email" &&
      method !== "magic_link" &&
      method !== "google" &&
      method !== "invitation"
    ) {
      return null;
    }
    return typeof completed === "boolean" ? { method, completed } : null;
  } catch {
    return null;
  }
}

function writeProgress(progress: RegistrationProgress | null): void {
  try {
    if (progress) sessionStorage.setItem(REGISTRATION_STORAGE_KEY, JSON.stringify(progress));
    else sessionStorage.removeItem(REGISTRATION_STORAGE_KEY);
  } catch {
    // Disabled storage only removes redirect continuity; same-page events work.
  }
}

function wasRegistrationReported(): boolean {
  try {
    return sessionStorage.getItem(REGISTRATION_REPORTED_KEY) === "1";
  } catch {
    return false;
  }
}

function setRegistrationReported(reported: boolean): void {
  try {
    if (reported) sessionStorage.setItem(REGISTRATION_REPORTED_KEY, "1");
    else sessionStorage.removeItem(REGISTRATION_REPORTED_KEY);
  } catch {
    // In-memory flow still works; storage only protects redirects and reloads.
  }
}

export function trackSignupStarted(method: RegistrationMethod): void {
  const current = readProgress();
  setRegistrationReported(false);
  writeProgress({ method, completed: false });
  if (current?.method === method) return;
  captureEvent("signup_started", { method });
}

export function trackSignupVerificationRequested(): void {
  captureEvent("signup_verification_requested", { method: "email" });
}

export function trackSignupVerified(method: RegistrationMethod): void {
  writeProgress({ method, completed: false });
  captureEvent("signup_verified", { method });
}

export function markRegistrationCompleted(method: RegistrationMethod): void {
  writeProgress({ method, completed: true });
}

function hasFirstRunMarker(): boolean {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).get("firstRun") === "1";
}

function removeFirstRunMarker(): void {
  if (typeof window === "undefined" || !hasFirstRunMarker()) return;
  const url = new URL(window.location.href);
  url.searchParams.delete("firstRun");
  window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
}

export function completedRegistrationMethod(
  progress: RegistrationProgress | null,
  firstRun: boolean,
): RegistrationMethod | null {
  if (progress?.completed) return progress.method;
  return firstRun ? "google" : null;
}

/**
 * Consumes a confirmed registration after identity has been queued.
 *
 * Google completes on the shore and returns with `firstRun=1`, so it has no
 * client-side completion callback. Every other path marks completion itself.
 */
export function captureCompletedRegistration(): void {
  const progress = readProgress();
  const firstRun = hasFirstRunMarker();
  if (wasRegistrationReported()) {
    removeFirstRunMarker();
    return;
  }
  const method = completedRegistrationMethod(progress, firstRun);
  if (!method) return;

  writeProgress(null);
  setRegistrationReported(true);
  removeFirstRunMarker();
  captureEvent("registration_completed", { method });
}
